import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { fmtApiError } from "@/lib/api";

const T = {
  page:        "signup-page",
  name:        "signup-name",
  email:       "signup-email",
  phone:       "signup-phone",
  pw:          "signup-password",
  pwConfirm:   "signup-password-confirm",
  submit:      "signup-submit",
  error:       "signup-error",
  success:     "signup-success",
};

export default function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password !== passwordConfirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await signup({ name: name.trim(), email: email.trim(), phone: phone.trim() || undefined, password });
      setDone(true);
    } catch (err) {
      setError(fmtApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-950 text-white relative overflow-hidden gold-aurora grain flex items-center justify-center px-6">
      <Link to="/" className="absolute top-6 left-6 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition">
        <ArrowLeft size={14} /> Back to site
      </Link>

      <div data-testid={T.page} className="w-full max-w-md glass relative z-10">
        <div className="p-8">
          <div className="mb-8">
            <span className="heading text-2xl font-extrabold tracking-tighter">
              AUREUS<span className="text-gold-grad">.AI</span>
            </span>
            <h1 className="heading text-3xl font-bold tracking-tighter mt-6">Request access</h1>
            <p className="text-zinc-400 text-sm mt-2">
              Accounts are admin-approved. You'll get terminal access once an admin reviews your request.
            </p>
          </div>

          {done ? (
            <div data-testid={T.success} className="space-y-5">
              <div className="flex items-center gap-3 text-bull">
                <CheckCircle size={28} weight="fill" />
                <span className="heading text-xl">Request submitted.</span>
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed">
                Your account <span className="mono text-white">{email}</span> is in the
                admin queue. You'll be able to sign in once it's approved.
              </p>
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="w-full inline-flex items-center justify-center gap-2 border border-white/20 px-6 py-3 hover:bg-white/5 transition"
              >
                Back to sign in <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5">
              <div>
                <label className="overline mb-2 block">Full Name</label>
                <input
                  data-testid={T.name}
                  type="text"
                  required
                  minLength={1}
                  maxLength={80}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 px-4 py-3 mono text-sm focus:border-gold/60 focus:outline-none transition"
                  placeholder="Jane Trader"
                />
              </div>
              <div>
                <label className="overline mb-2 block">Email</label>
                <input
                  data-testid={T.email}
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 px-4 py-3 mono text-sm focus:border-gold/60 focus:outline-none transition"
                  placeholder="you@desk.com"
                />
              </div>
              <div>
                <label className="overline mb-2 block">Phone number <span className="text-zinc-500">· with country code</span></label>
                <input
                  data-testid={T.phone}
                  type="tel"
                  autoComplete="tel"
                  required
                  pattern="^\+?[0-9 \-]{7,20}$"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 px-4 py-3 mono text-sm focus:border-gold/60 focus:outline-none transition"
                  placeholder="+91 98765 43210"
                />
              </div>
              <div>
                <label className="overline mb-2 block">Password <span className="text-zinc-500">· min 8 chars</span></label>
                <input
                  data-testid={T.pw}
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  maxLength={128}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 px-4 py-3 mono text-sm focus:border-gold/60 focus:outline-none transition"
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label className="overline mb-2 block">Confirm Password</label>
                <input
                  data-testid={T.pwConfirm}
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  maxLength={128}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  className={`w-full bg-black/40 border px-4 py-3 mono text-sm focus:outline-none transition ${
                    passwordConfirm && passwordConfirm !== password
                      ? "border-bear/60 focus:border-bear"
                      : "border-white/10 focus:border-gold/60"
                  }`}
                  placeholder="••••••••"
                />
                {passwordConfirm && passwordConfirm !== password && (
                  <p className="mt-1 text-xs text-bear">Passwords do not match.</p>
                )}
              </div>

              {error && (
                <div data-testid={T.error} className="text-sm text-bear bg-bear/10 border border-bear/30 px-4 py-3">
                  {error}
                </div>
              )}

              <button
                type="submit"
                data-testid={T.submit}
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black px-6 py-3 font-medium hover:bg-yellow-400 transition disabled:opacity-50"
              >
                {submitting ? "Submitting…" : (<>Request Access <ArrowRight size={16} /></>)}
              </button>
            </form>
          )}

          <div className="mt-8 pt-6 border-t border-white/10 text-sm text-zinc-400">
            Already approved?{" "}
            <Link to="/login" className="text-gold hover:text-yellow-400 underline-offset-2 hover:underline">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
